import { createContext, useState, ReactNode, useEffect } from "react";

interface PmsItem {
  id: number;
  name: string;
  type: string;
}

interface PropertyContextType {
  propertyId: string;
  setPropertyId: (id: string) => void;
  propertyCode: string;
  setPropertyCode: (code: string) => void;
  propertyName: string;
  setPropertyName: (name: string) => void;
  clientID: string | null;
  setClientID: (id: string | null) => void;
  accessKey: string | null;
  setAccessKey: (key: string | null) => void;
  pmsList: PmsItem[];
  setPmsList: (list: PmsItem[]) => void;
  clearPropertySelection: () => void;
}

export const PropertyContext = createContext<PropertyContextType>({
  propertyId: "",
  setPropertyId: () => {},
  propertyCode: "",
  setPropertyCode: () => {},
  propertyName: "",
  setPropertyName: () => {},
  clientID: null,
  setClientID: () => {},
  accessKey: null,
  setAccessKey: () => {},
  pmsList: [],
  setPmsList: () => {},
  clearPropertySelection: () => {},
});

export const PropertyProvider = ({ children }: { children: ReactNode }) => {
  const [propertyId, setPropertyId] = useState<string>(localStorage.getItem("selectedPropertyId") || "");
  const [propertyCode, setPropertyCode] = useState<string>(localStorage.getItem("selectedPropertyCode") || "");
  const [propertyName, setPropertyName] = useState<string>(localStorage.getItem("selectedPropertyName") || "");
  const [clientID, setClientID] = useState<string | null>(localStorage.getItem("selectedClientID") || null);
  const [accessKey, setAccessKey] = useState<string | null>(localStorage.getItem("selectedAccessKey") || null);
  
  const [pmsList, setPmsList] = useState<PmsItem[]>(() => {
    const saved = localStorage.getItem("selectedPmsList");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (propertyId) localStorage.setItem("selectedPropertyId", propertyId); else localStorage.removeItem("selectedPropertyId");
    if (propertyCode) localStorage.setItem("selectedPropertyCode", propertyCode); else localStorage.removeItem("selectedPropertyCode");
    if (propertyName) localStorage.setItem("selectedPropertyName", propertyName); else localStorage.removeItem("selectedPropertyName");
    if (clientID) localStorage.setItem("selectedClientID", clientID); else localStorage.removeItem("selectedClientID");
    if (accessKey) localStorage.setItem("selectedAccessKey", accessKey); else localStorage.removeItem("selectedAccessKey");
    
    if (pmsList.length > 0) {
      localStorage.setItem("selectedPmsList", JSON.stringify(pmsList));
    } else {
      localStorage.removeItem("selectedPmsList");
    }
  }, [propertyId, propertyCode, propertyName, clientID, accessKey, pmsList]);

  const clearPropertySelection = () => {
    setPropertyId(""); setPropertyCode(""); setPropertyName(""); setClientID(null); setAccessKey(null); setPmsList([]);
    localStorage.removeItem("selectedPropertyId");
    localStorage.removeItem("selectedPropertyCode");
    localStorage.removeItem("selectedPropertyName");
    localStorage.removeItem("selectedClientID");
    localStorage.removeItem("selectedAccessKey");
    localStorage.removeItem("selectedPmsList");
  };

  return (
    <PropertyContext.Provider value={{
      propertyId, setPropertyId,
      propertyCode, setPropertyCode,
      propertyName, setPropertyName,
      clientID, setClientID,
      accessKey, setAccessKey,
      pmsList, setPmsList,
      clearPropertySelection
    }}>
      {children}
    </PropertyContext.Provider>
  );
};